import axios from 'axios';

const API_URL = '/api/tenant/print-bridge';

/**
 * Servicio del puente de impresión Windows.
 * El celular/navegador HTTPS habla solo con el backend; el complemento
 * Windows mantiene el WebSocket saliente.
 */

/** Genera un código de vinculación de un solo uso. */
export const createPairingCode = async (suggestedName) => {
  const response = await axios.post(`${API_URL}/pairing-codes`, {
    suggestedName: suggestedName || undefined,
  });
  return response.data;
};

/** Lista puentes del negocio. */
export const listPrintBridges = async () => {
  const response = await axios.get(`${API_URL}/bridges`);
  return response.data;
};

/** Renombra un puente. */
export const renamePrintBridge = async (id, name) => {
  const response = await axios.patch(`${API_URL}/bridges/${encodeURIComponent(id)}`, { name });
  return response.data;
};

/** Revoca un puente (invalida el token del dispositivo). */
export const revokePrintBridge = async (id) => {
  const response = await axios.post(`${API_URL}/bridges/${encodeURIComponent(id)}/revoke`);
  return response.data;
};

/** Catálogo plano de impresoras anunciadas por puentes activos. */
export const listRemotePrinters = async () => {
  const response = await axios.get(`${API_URL}/printers`);
  return response.data;
};

/**
 * Crea un trabajo de impresión RAW, PDF o HTML.
 * @param {{ bridgeId: number|string, printerName: string, format: 'raw'|'pdf'|'html', dataBase64: string, documentName?: string, paperWidthMm?: 58|80, jobId?: string }} payload
 */
export const createPrintJob = async (payload) => {
  const response = await axios.post(`${API_URL}/jobs`, payload);
  return response.data;
};

/** Convierte HTML UTF-8 a Base64 para el puente Windows. */
export const htmlToBase64 = (html) => {
  const bytes = new TextEncoder().encode(String(html || ''));
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary);
};

/** Convierte un Blob/ArrayBuffer binario (por ejemplo PDF) a Base64. */
export const binaryToBase64 = async (value) => {
  const buffer = value instanceof Blob ? await value.arrayBuffer() : value;
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
};

/** Consulta el estado de un trabajo. */
export const getPrintJob = async (jobId) => {
  const response = await axios.get(`${API_URL}/jobs/${encodeURIComponent(jobId)}`);
  return response.data;
};

/**
 * Espera a que el trabajo termine (completed/failed/expired) con polling.
 * @returns {Promise<object>} job
 */
export const waitForPrintJob = async (jobId, { timeoutMs = 90000, intervalMs = 1000 } = {}) => {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    // eslint-disable-next-line no-await-in-loop
    const { job } = await getPrintJob(jobId);
    if (['completed', 'failed', 'expired'].includes(job.status)) {
      return job;
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  const { job } = await getPrintJob(jobId);
  return job;
};
